// core
import { Component, Input, OnInit } from '@angular/core';
import { FormGroup } from '@angular/forms';

// Raptor
import { RaptorActionService } from '../../resources/raptor-action.service';
import { RaptorStepDataService } from '../../services/raptor-step-data.service';
import { RAPTOR_DEBOUNCE_TIME } from '../../constant/raptor-constants';

@Component({
  selector: 'open-raptor-vehicle-select',
  templateUrl: './raptor-vehicle-select.component.html',
  styleUrls: ['./raptor-vehicle-select.component.scss']
})
export class RaptorVehicleSelectComponent implements OnInit {
  @Input() question!: any;
  @Input() form!: FormGroup;

  questionControl: any;
  mode: string = 'rego';
  regoSearch?: string;
  regoFailed: boolean = false;
  vehicleData!: Array<any>;
  searchValues: any = {};
  selectedCar: any = {};
  iconType!: string;

  constructor(
    private raptorActionService: RaptorActionService,
    private raptorStepDataService: RaptorStepDataService
  ) { }

  ngOnInit(): void {
    this.questionControl = this.form.get(this.question.key);

    if (this.question.settings && this.question.settings.icon) {
      this.iconType = this.question.settings.icon.split('.').pop().toLowerCase();
    }

    if (this.questionControl.value) {
      const stepData = this.raptorStepDataService.get();
      const label = [
        stepData.data[`${this.question.key}_year`],
        stepData.data[`${this.question.key}_make`],
        stepData.data[`${this.question.key}_model`],
        stepData.data[`${this.question.key}_variant`],
        stepData.data[`${this.question.key}_series`]
      ].join(' ');
      const text = [
        stepData.data[`${this.question.key}_description`],
        '-',
        stepData.data[`${this.question.key}_transmission`]
      ].join(' ');

      // show the selected car
      this.selectedCar = {
        label: label,
        text: text
      };
      this.changeMode('preselected', false);
    } else {
      this.changeMode('rego', true);
    }
  }

  public changeMode(mode: string, lookup: boolean) {
    this.mode = mode;
    this.searchValues = {};
    this.vehicleData = [];

    if (lookup) {
      this.lookupValues();
    }
    // manually set search value properties in order to show the placeholder
    if (mode === 'rego') {
      this.regoSearch = '';
      this.searchValues.state = null;
    }
    if (mode === 'manual') {
      this.searchValues.make = null;
      this.searchValues.model = null;
      this.searchValues.transmission = null;
      this.searchValues.variant = null;
      this.searchValues.year = null;
    }
  }

  public lookupRego() {
    this.regoSearch = this.searchValues.rego;
    this.regoFailed = false;
    this.lookupValues();
  }

  public lookupValues(field?: string): void {
    let searchParams: Array<string> = [];
    const stepData = this.raptorStepDataService.get();
    let searchQuery: string = '';
    const manualKeys = ['year', 'make', 'model', 'variant', 'transmission', 'vehicle'];
    let locked_make: boolean = false;

    // if key exists sanitise the manual lookup query
    if (field) {
      const fieldIndex = manualKeys.indexOf(field) + 1;
      if (fieldIndex > -1) {
        for (let i = fieldIndex; i <= manualKeys.length; i++) {
          this.searchValues[manualKeys[i]] = null;
        }
      }
    }

    for (const key in this.searchValues) {
      if (this.searchValues[key] || this.searchValues[key] === '') {
        searchParams.push(`${key}=${encodeURIComponent(this.searchValues[key])}`);
      }
    }
    
    if ( Array.isArray(this.question.settings.disabled) && this.question.settings.disabled.length > 0 ) {
      locked_make = this.question.settings.disabled.includes("make")
    }

    if (locked_make && stepData?.data && stepData.data.vehicle_make) {
      searchParams.push(`make=${stepData.data[`${this.question.key}_make`]}`);
    }
    
    if (searchParams.length) {
      searchQuery = `?${searchParams.join('&')}`;
    }
    const url = `${this.question.settings.autocomplete_url}${searchQuery}`;

    this.raptorActionService.getAutoCompleteChoices(url).subscribe((data: any) => {
      this.vehicleData = this.mapData(data);
      if (data.variant && data.variant.length === 1 && data.transmission.length === 0) {
        this.searchValues.variant = data.variant[0].value;
        this.lookupValues('variant');
      }

      if (locked_make && data.make && data.make.length > 0 && stepData.data.vehicle_make && data.model.length === 0) {
        const filterVehicleList = data.make.filter((vehicle: any) => vehicle.value === stepData.data.vehicle_make);
        
        if(filterVehicleList.length > 0) {
          this.searchValues.make = stepData.data.vehicle_make;
          this.lookupValues('model');
        }
      }

      // if there is only one vehicle then preselect it
      if (data.vehicle && data.vehicle.length === 1) {
        this.searchValues.vehicle = data.vehicle[0].value;
        this.selectVehicle(data.vehicle[0], false);
      } else if (this.regoSearch && !this.regoFailed) {
        // set the rego fail message
        this.regoFailed = true;
        this.changeMode('manual', true);
      }
    });
  }

  mapData(data: any): any {
    let result = [];

    if (data) {
      for (const key in data) {
        const item = {
          key,
          value: (key === 'make' && data[key] && data[key].length) ? arrangeMakeGroups(data[key]) : data[key]
        };
        data[key].forEach((value: any) => {
          if (value.selected) {
            this.searchValues[key] = value.value;
          }
        });
        result.push(item);
      }
    }

    function arrangeMakeGroups(makes: Array<any>) {
      let optionGroups: any = [
        {
          label: 'Popular makes',
          value: []
        },
        {
          label: 'All makes',
          value: []
        }
      ];

      makes.forEach((make: any) => {
        if (make.is_popular) {
          optionGroups[0].value.push(make);
        } else {
          optionGroups[1].value.push(make);
        }
      });
      return optionGroups;
    }
    return result;
  }

  public selectVehicle(option: any, willScroll: boolean) {
    if (willScroll) {
      this.scrollTo('button[type="submit"]');
    }

    setTimeout(() => {
      this.questionControl.setValue(this.searchValues, { emitEvent: true });
    }, RAPTOR_DEBOUNCE_TIME);
  }

  public scrollReset() {
    this.changeMode(this.mode, true);
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'smooth'
    });
  }

  scrollTo(selector: string) {
    const element: any = document.querySelector(selector);
    const position = element?.offsetTop;

    window.scrollTo({
      top: position,
      left: 0,
      behavior: 'smooth'
    });
  }
  
  isFieldDisabled(field: any, question?: any) {
    let disableField = false;
    if (question && question.settings.disabled) {
      disableField = question.settings.disabled[0] === field.key;
    }
    return !field.value || field.value.length === 0 || disableField;
  }
}
